<template>
  <div class="company-selector">
    <label v-if="label" :for="id" class="selector-label">{{ label }}</label>
    <div class="selector-wrapper" ref="selectorRef">
      <div
        class="selector-input"
        :class="{ 'open': isOpen, 'disabled': disabled || loading }"
        @click="toggleDropdown"
      >
        <input
          v-if="isOpen"
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="search-input"
          :placeholder="placeholder"
          @keydown.esc="closeDropdown"
          @keydown.enter.prevent="selectFirst"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
        />
        <div v-else class="selected-value" @click.stop="toggleDropdown">
          {{ selectedCompanyName || placeholder }}
        </div>
        <font-awesome-icon
          icon="fa-solid fa-chevron-down"
          class="dropdown-icon"
          :class="{ 'rotate': isOpen }"
          @click.stop="toggleDropdown"
        />
      </div>

      <div v-if="isOpen" class="dropdown-menu">
        <div v-if="loading" class="dropdown-item loading">
          <span class="spinner-small"></span>
          {{ $t('common.loadingCompanies') }}
        </div>
        <div v-else-if="filteredCompanies.length === 0 && companies.length === 0" class="dropdown-item empty-with-action">
          <div class="empty-message">{{ $t('companies.noCompaniesYet') }}</div>
          <button @click.stop="createDefaultCompany" class="btn-create-default" :disabled="creatingDefault">
            <font-awesome-icon v-if="creatingDefault" icon="fa-solid fa-spinner" spin />
            <font-awesome-icon v-else icon="fa-solid fa-plus" />
            {{ creatingDefault ? $t('common.creating') : $t('companies.startWithDefaultCompany') }}
          </button>
        </div>
        <div v-else-if="filteredCompanies.length === 0" class="dropdown-item empty">
          {{ $t('companies.noCompaniesFound') }}
        </div>
        <div v-else class="dropdown-list">
          <div
            class="dropdown-item clear-option"
            :class="{
              'selected': !selectedCompany,
              'highlighted': highlightedIndex === -1
            }"
            @click="clearSelection"
            @mouseenter="highlightedIndex = -1"
          >
            <div class="company-name">{{ $t('companies.allCompanies') }}</div>
          </div>
          <div
            v-for="(company, index) in filteredCompanies"
            :key="company.id"
            class="dropdown-item"
            :class="{
              'selected': company.id === selectedCompany,
              'highlighted': index === highlightedIndex
            }"
            @click="selectCompany(company)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="company-name">{{ company.name }}</div>
            <div v-if="company.vat_number" class="company-vat">{{ company.vat_number }}</div>
          </div>
        </div>
      </div>
    </div>
    <span v-if="error" class="selector-error">{{ error }}</span>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCompanies } from '@/composables/useCompanyVendor';
import api from '@/utils/api';

const props = defineProps({
  modelValue: {
    type: [Number, String, null],
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Select Company'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  autoLoad: {
    type: Boolean,
    default: true
  },
  id: {
    type: String,
    default: 'company-selector'
  },
  excludeId: {
    type: [Number, null],
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();
const { companies, loading, error, loadCompanies } = useCompanies();

const isOpen = ref(false);
const searchQuery = ref('');
const selectorRef = ref(null);
const searchInput = ref(null);
const highlightedIndex = ref(0);
const creatingDefault = ref(false);

const selectedCompany = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const selectedCompanyName = computed(() => {
  if (!companies.value || companies.value.length === 0) return '';
  const company = companies.value.find(c => c.id === selectedCompany.value);
  return company ? company.name : '';
});

const filteredCompanies = computed(() => {
  if (!companies.value) return [];

  let filtered = companies.value;

  // Only show active companies
  filtered = filtered.filter(c => c.is_active);

  // Exclude specific company ID if provided
  if (props.excludeId) {
    filtered = filtered.filter(c => c.id !== props.excludeId);
  }

  if (!searchQuery.value) {
    return filtered;
  }

  const query = searchQuery.value.toLowerCase();
  return filtered.filter(company => {
    const nameMatch = company.name?.toLowerCase().includes(query);
    const vatMatch = company.vat_number?.toLowerCase().includes(query);
    return nameMatch || vatMatch;
  });
});

const toggleDropdown = () => {
  if (props.disabled || loading.value) return;

  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    searchQuery.value = '';
    highlightedIndex.value = 0;
    nextTick(() => {
      searchInput.value?.focus();
    });
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchQuery.value = '';
  highlightedIndex.value = 0;
};

const selectCompany = (company) => {
  selectedCompany.value = company.id;
  emit('change', company);
  closeDropdown();
};

const clearSelection = () => {
  selectedCompany.value = null;
  emit('change', null);
  closeDropdown();;
};

const selectFirst = () => {
  if (filteredCompanies.value.length > 0) {
    selectCompany(filteredCompanies.value[0]);
  }
};

const navigateDown = () => {
  if (highlightedIndex.value < filteredCompanies.value.length - 1) {
    highlightedIndex.value++;
    scrollToHighlighted();
  }
};

const navigateUp = () => {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
    scrollToHighlighted();
  }
};

const scrollToHighlighted = () => {
  nextTick(() => {
    const highlighted = document.querySelector('.dropdown-item.highlighted');
    if (highlighted) {
      highlighted.scrollIntoView({ block: 'nearest' });
    }
  });
};

const createDefaultCompany = async () => {
  creatingDefault.value = true;
  try {
    const response = await api.post('/api/companies', {
      name: t('companies.myCompany'),
      is_active: true
    });
    
    // Reload companies list with force=true to ensure refresh
    await loadCompanies(true);
    
    // Select the newly created company
    const newCompany = response.data.company || response.data;
    selectedCompany.value = newCompany.id;
    emit('change', newCompany);
    closeDropdown();
  } catch (err) {
    console.error('Failed to create default company:', err);
    alert(t('companies.createFailed'));
  } finally {
    creatingDefault.value = false;
  }
};

const handleClickOutside = (event) => {
  if (selectorRef.value && !selectorRef.value.contains(event.target)) {
    closeDropdown();
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onMounted(() => {
  if (props.autoLoad) {
    loadCompanies();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Expose methods for parent components
defineExpose({
  loadCompanies,
  companies
});
</script>

<style scoped>
.company-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selector-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--gray-dark);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.selector-wrapper {
  position: relative;
}

.selector-input {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px 40px 10px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 42px;
}

.selector-input:hover:not(.disabled) {
  border-color: var(--gray-light);
}

.selector-input.open {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}

.company-selector.active-filter .selector-input:not(.open) {
  border: 2px solid #0ea5e9;
  background: rgba(14, 165, 233, 0.05);
}

.selector-input.disabled {
  background: var(--bg-gray);
  cursor: not-allowed;
  opacity: 0.6;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--text-primary);
  background: transparent;
  padding: 0;
}

.search-input::placeholder {
  color: var(--text-light);
}

.selected-value {
  flex: 1;
  font-size: 14px;
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selector-input:not(.open) .selected-value:empty::before {
  content: attr(data-placeholder);
  color: var(--text-light);
}

.dropdown-icon {
  position: absolute;
  right: 14px;
  flex-shrink: 0;
  color: var(--text-secondary);
  transition: transform 0.2s ease;
  font-size: 12px;
  pointer-events: none;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 300px;
  background: white;
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.dropdown-list {
  max-height: 300px;
  overflow-y: auto;
}

.dropdown-item {
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--gray-lighter);
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: var(--primary-light);
}

.dropdown-item.selected {
  background: var(--primary-light);
  font-weight: 600;
}

.dropdown-item.clear-option {
  font-style: italic;
  color: var(--text-secondary);
  border-bottom: 2px solid var(--border);
}

.dropdown-item.clear-option.selected {
  font-style: normal;
  font-weight: 600;
  color: var(--text-primary);
}

.dropdown-item.loading,
.dropdown-item.empty {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  color: var(--text-secondary);
  cursor: default;
  padding: 16px;
}

.dropdown-item.empty-with-action {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  color: var(--text-secondary);
  cursor: default;
  padding: 16px;
}

.empty-message {
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
}

.btn-create-default {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(14, 165, 233, 0.3);
}

.btn-create-default:hover:not(:disabled) {
  background: linear-gradient(135deg, #0284c7 0%, #2563eb 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.btn-create-default:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-create-default svg {
  flex-shrink: 0;
}

.dropdown-item.loading:hover,
.dropdown-item.empty:hover,
.dropdown-item.empty-with-action:hover {
  background: white;
}

.company-name {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.company-vat {
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.selector-error {
  font-size: 12px;
  color: var(--danger);
  font-style: italic;
}
</style>

